import { useCallback, useMemo, useState } from 'react';
import { CalendarClock, Loader2, Trophy, Grid3X3 } from 'lucide-react';
import { appendLobbyReturnToPlayUrl } from '@low6-pickem/frontend-shared';
import { buildSquaresContestPlayUrl } from '../utils/squaresPlayUrl';
import {
  useSquaresMyPicks,
  useSquaresFixtureScoresBatch,
  usePickemPlay,
  usePickemRecent,
  useDailyRevealGames,
  useDailyRevealGamesBatch,
  useDailyRevealFixtures,
  useDailyRevealFixturesBatch,
  useSurvivorContests,
  useSurvivorMyPicksBatch,
  useSurvivorContestsBatch,
  useSurvivorStatsBatch,
  useBingoContests,
  useBingoCardsBatch,
  type SquaresMyPick,
  type PickemItem,
  type DRGetGameResponse,
  type DRDaySummary,
  type DRDayFixture,
  type SurvivorContestDetail,
  type SurvivorMyPicks,
  type SurvivorStats,
  type BingoMyCard,
} from '../api/getMyGames';
import type { LobbyGame } from '../api/getLobby';
import { getStoredToken } from '../utils/auth';
import {
  buildDailyRevealContestUrl,
  findDRGameIdForDay,
  isBingoLobbyGame,
  isSurvivorLobbyGame,
  resolveBingoPublicUrl,
  resolvePickemPublicUrl,
  resolveSurvivorPublicUrl,
} from '../utils/lobbyMyGamesNavigation';
import {
  buildSurvivorMyGames,
  isSurvivorEntryOver,
  type SurvivorMyGame,
} from '../utils/lobbyMyGamesSurvivor';
import {
  buildBingoMyGames,
  isBingoContestComplete,
  isBingoGameLive,
  type BingoMyGame,
} from '../utils/lobbyMyGamesBingo';
import { mapSquaresFixtureScores, squaresContestScoreId } from '../utils/lobbyMyGamesSquaresScores';
import {
  buildPastGameFilters,
  buildPastResultItems,
  filterPastResultItems,
  pastItemKey,
  type PastGameFilter,
  type PastResultItem,
} from '../utils/lobbyMyGamesPast';
import {
  buildDailyRevealContestWeekOptions,
  filterDailyRevealItemsByContestWeek,
  resolveSelectedContestWeekId,
} from '../utils/lobbyMyGamesDailyRevealContest';
import { dailyRevealProductName } from '../utils/dailyRevealProductName';
import {
  DEFAULT_MY_GAMES_TAB,
  MY_GAMES_TABS,
  classifyDailyRevealDayTab,
  isPickemLive,
  isSquaresPickLive,
  isSurvivorGameLive,
  type MyGamesTab,
} from '../utils/lobbyMyGamesTabs';
import { readTeamAssetFolder, type TeamAssetFolderId } from '../utils/teamAssetFolder';
import LobbySquaresCard from './my-games/LobbySquaresCard';
import LobbyPickemCard from './my-games/LobbyPickemCard';
import LobbySurvivorCard from './my-games/LobbySurvivorCard';
import LobbyBingoCard from './my-games/LobbyBingoCard';
import DailyRevealContestWeekSelect from './my-games/DailyRevealContestWeekSelect';
import {
  LobbyDailyRevealTodaysPick,
  LobbyDailyRevealWeeklyCard,
} from './my-games/LobbyDailyRevealCard';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LobbyMyGamesViewProps {
  /** Lobby games — used to resolve public_url for navigation */
  lobbyGames?: LobbyGame[];
  /**
   * Active sub-tab. Controlled by LobbyView so it can live in the URL (`?tab=live`);
   * omit both props and the view falls back to local state.
   */
  activeTab?: MyGamesTab;
  onTabChange?: (tab: MyGamesTab) => void;
  /** Switches the primary tab back to the lobby — the CTA on every empty state. */
  onBrowseLobby?: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildPlayUrl(publicUrl: string | null): string | null {
  if (!publicUrl) return null;
  const url = appendLobbyReturnToPlayUrl(publicUrl);
  const token = getStoredToken();
  if (!token) return url;
  return `${url}&token=${encodeURIComponent(token)}`;
}

/** Play deep-link uses pickem id as the contestId query param value. */
function resolveSquaresPickemId(pick: SquaresMyPick, lobbyGames: LobbyGame[]): string | null {
  const fromPick = pick.pickem_id as string | undefined;
  if (fromPick) return fromPick;

  const questionId = (pick.question_id as string | undefined) ?? pick.squares_contest_id;
  const lobbyMatch = lobbyGames.find(g => {
    if (g.game_type !== 'squares' || !g.public_url || !questionId) return false;
    return g.public_url.includes(questionId);
  });
  return lobbyMatch?.pickem_id ?? null;
}

function buildSquaresPickPlayUrl(pick: SquaresMyPick, lobbyGames: LobbyGame[]): string | null {
  const pickemId = resolveSquaresPickemId(pick, lobbyGames);
  if (!pickemId) return null;
  // The contest's own public_url carries the Squares host. The lobby listing is the fallback and
  // it is empty for any contest the lobby no longer shows, which is most of My Games.
  return buildPlayUrl(buildSquaresContestPlayUrl(pickemId, lobbyGames, pick.public_url));
}

function isSquaresActive(pick: SquaresMyPick): boolean {
  const s = (pick.status ?? '').toUpperCase();
  return s !== 'COMPLETE' && pick.status !== 'final' && pick.status !== 'completed';
}

function isSquaresCompleted(pick: SquaresMyPick): boolean {
  const s = (pick.status ?? '').toUpperCase();
  return s === 'COMPLETE' || pick.status === 'final' || pick.status === 'completed';
}

function mapPickemGameState(pickem: PickemItem): 'upcoming' | 'countdown' | 'live' | 'final' {
  const s = String(pickem.status ?? '').toUpperCase();
  if (s === 'COMPLETE' || s === 'COMPLETED' || s === 'FINAL' || s === '2') return 'final';
  if (s === 'INPLAY' || s === 'LIVE' || s === '1') return 'live';
  const gameDate = pickem.start
    ? new Date(pickem.start as string)
    : pickem.start_utc_timestamp
      ? new Date((pickem.start_utc_timestamp as number) * 1000)
      : null;
  if (gameDate) {
    const diff = gameDate.getTime() - Date.now();
    if (diff <= 0) return 'live';
    if (diff <= 86_400_000) return 'countdown';
  }
  return 'upcoming';
}

/**
 * Collect every Daily Reveal day the user has a selection on. My Games used to
 * promote a single "active" contest and dump every other pick into Recent — that
 * put still-scheduled NFL fixtures under Recent whenever another sport's day was
 * preferred. Each selected day is classified on its own now.
 */
function collectSelectedDRDays(gameDetails: Array<DRGetGameResponse | undefined>): DRDaySummary[] {
  const days: DRDaySummary[] = [];
  for (const game of gameDetails) {
    if (!game) continue;
    for (const day of game.days) {
      if (day.userEntry?.selectedCard) days.push(day);
    }
  }
  return days;
}

// ─── Daily Reveal sub-sections ────────────────────────────────────────────────

interface DRTodaysSectionProps {
  currentDay: DRDaySummary | null;
  sport?: string | null;
  leagueIpEnabled?: boolean;
  teamAssetFolder?: TeamAssetFolderId | null;
  targetUrl?: string | null;
}

function DRTodaysPickSection({
  currentDay,
  sport,
  leagueIpEnabled,
  teamAssetFolder,
  targetUrl,
}: DRTodaysSectionProps) {
  const dayId = currentDay?.id ?? null;
  const { data: fixtures = [] } = useDailyRevealFixtures(dayId);

  if (!currentDay?.userEntry?.selectedCard) return null;

  return (
    <LobbyDailyRevealTodaysPick
      day={currentDay}
      fixtures={fixtures}
      sport={sport}
      leagueIpEnabled={leagueIpEnabled}
      teamAssetFolder={teamAssetFolder}
      targetUrl={targetUrl}
    />
  );
}

interface DRPastDayCardProps {
  day: DRDaySummary;
  sport?: string | null;
  leagueIpEnabled?: boolean;
  teamAssetFolder?: TeamAssetFolderId | null;
  targetUrl?: string | null;
}

function DRPastDayCard({
  day,
  sport,
  leagueIpEnabled,
  teamAssetFolder,
  targetUrl,
}: DRPastDayCardProps) {
  const { data: fixtures = [] } = useDailyRevealFixtures(day.id);
  return (
    <LobbyDailyRevealWeeklyCard
      day={day}
      fixtures={fixtures as DRDayFixture[]}
      sport={sport}
      leagueIpEnabled={leagueIpEnabled}
      teamAssetFolder={teamAssetFolder}
      targetUrl={targetUrl}
    />
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({
  icon,
  title,
  message,
  actionLabel,
  onAction,
}: {
  icon: React.ReactNode;
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-white font-semibold text-base mb-1">{title}</h3>
      <p className="text-zinc-500 text-sm max-w-xs">{message}</p>
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="mt-5 rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-black transition hover:bg-white/85"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function LobbyMyGamesView({
  lobbyGames = [],
  activeTab: controlledTab,
  onTabChange,
  onBrowseLobby,
}: LobbyMyGamesViewProps) {
  const [uncontrolledTab, setUncontrolledTab] = useState<MyGamesTab>(DEFAULT_MY_GAMES_TAB);
  const activeTab = controlledTab ?? uncontrolledTab;
  const setActiveTab = useCallback(
    (tab: MyGamesTab) => {
      setUncontrolledTab(tab);
      onTabChange?.(tab);
    },
    [onTabChange]
  );
  const [pastGameFilter, setPastGameFilter] = useState<PastGameFilter>('all');
  /**
   * Contest week for a Daily Reveal product filter chip (or the lone product on an All-only-DR
   * list). Keyed by product label so switching chips keeps each week's choice.
   */
  const [contestWeekByProduct, setContestWeekByProduct] = useState<Record<string, string>>({});

  // ── Build pickem_id → public_url lookup from lobby games (used for Pickem) ──
  const publicUrlMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of lobbyGames) {
      if (g.pickem_id && g.public_url) {
        map.set(g.pickem_id, g.public_url);
      }
    }
    return map;
  }, [lobbyGames]);

  // ── Find daily_reveal game IDs ──
  // Merge lobby-listed DR games with the tenant-wide list from /api/daily-reveals:
  // contests hidden from the lobby (direct-URL entry) are absent from lobbyGames
  // but must still show in My Games once the user has joined them.
  const { data: allDRGames = [], isLoading: drListLoading } = useDailyRevealGames();
  const drGameIds = useMemo(() => {
    const ids = new Set<string>();
    for (const g of lobbyGames) {
      if (g.game_type === 'daily_reveal' || g.game_type === 'daily_draw') ids.add(g.pickem_id);
    }
    for (const g of allDRGames) ids.add(g.id);
    return [...ids];
  }, [lobbyGames, allDRGames]);

  // ── Fetch data ──
  const { data: squaresPicks = [], isLoading: squaresLoading } = useSquaresMyPicks();
  const squaresScoreIds = useMemo(
    () => squaresPicks.map(squaresContestScoreId).filter((id): id is string => Boolean(id)),
    [squaresPicks]
  );
  const { data: squaresFixtureScores } = useSquaresFixtureScoresBatch(squaresScoreIds);
  const squaresScoresByContestId = useMemo(() => {
    const fixtures = squaresFixtureScores?.fixtures ?? {};
    const map = new Map<string, ReturnType<typeof mapSquaresFixtureScores>>();
    for (const pick of squaresPicks) {
      const contestId = squaresContestScoreId(pick);
      if (!contestId) continue;
      map.set(contestId, mapSquaresFixtureScores(pick, fixtures[contestId]));
    }
    return map;
  }, [squaresPicks, squaresFixtureScores?.fixtures]);

  const { data: pickemPlay = [], isLoading: pickemPlayLoading } = usePickemPlay();
  const { data: pickemRecent = [], isLoading: pickemRecentLoading } = usePickemRecent();

  const drGameResults = useDailyRevealGamesBatch(drGameIds);
  const drGamesData = drGameResults.map(r => r.data);
  const drGamesLoading = drListLoading || drGameResults.some(r => r.isLoading);

  // Sport / contest of the day. A day carries neither of its own, and the player-facing product
  // name (Gridiron Draw / Big League Draw / Full Court Draw) plus the Recent week filter both
  // resolve through the owning contest.
  //
  // Plain derivation, not `useMemo`: `useQueries` hands back a fresh results array on every
  // render, so a memo keyed on it would recompute regardless.
  const drSportByDayId = new Map<string, string>();
  const drGameIdByDayId = new Map<string, string>();
  for (const game of drGamesData) {
    if (!game) continue;
    for (const day of game.days) {
      drSportByDayId.set(day.id, game.game?.sport ?? '');
      drGameIdByDayId.set(day.id, game.game.id);
    }
  }

  // ── Survivor ──
  // Same shape as Daily Reveal above, and for the same reason: Survivor serves no "my games"
  // endpoint, so membership is resolved contest by contest. The tenant list is merged with any
  // Survivor contest on the lobby listing, since a contest entered by direct link need not be
  // listed and one listed for entry need not be in the tenant list the app boots from.
  const { data: survivorContests = [], isLoading: survivorListLoading } = useSurvivorContests();
  const survivorSummaries = useMemo(() => {
    const byId = new Map(survivorContests.map(contest => [contest.id, contest]));
    for (const game of lobbyGames) {
      if (!isSurvivorLobbyGame(game) || byId.has(game.pickem_id)) continue;
      byId.set(game.pickem_id, {
        id: game.pickem_id,
        titleEn: game.title_en,
        sport: game.sport ?? '',
        gameType: 'survivor',
        pickemStatus: game.status,
        startUtc: game.start_utc ?? null,
      });
    }
    return [...byId.values()];
  }, [survivorContests, lobbyGames]);

  const survivorIds = useMemo(() => survivorSummaries.map(c => c.id), [survivorSummaries]);
  const survivorPickResults = useSurvivorMyPicksBatch(survivorIds);

  // Plain derivations, not `useMemo`: `useQueries` hands back a fresh results array on every
  // render, so a memo keyed on it would recompute regardless — and these loops run over the
  // handful of Survivor contests a tenant has, not the pool of players a game screen deals in.
  const survivorMyPicksById = new Map<string, SurvivorMyPicks>();
  survivorIds.forEach((id, index) => {
    const data = survivorPickResults[index]?.data;
    if (data) survivorMyPicksById.set(id, data);
  });

  // Config + weeks, only for the contests the user turned out to be in — see the hook's docs.
  const survivorEnteredIds = survivorIds.filter(id => survivorMyPicksById.get(id)?.entry);
  const survivorDetailResults = useSurvivorContestsBatch(survivorEnteredIds);
  const survivorDetailById = new Map<string, SurvivorContestDetail>();
  survivorEnteredIds.forEach((id, index) => {
    const data = survivorDetailResults[index]?.data;
    if (data) survivorDetailById.set(id, data);
  });

  const survivorStatsResults = useSurvivorStatsBatch(survivorEnteredIds);
  const survivorStatsById = new Map<string, SurvivorStats>();
  survivorEnteredIds.forEach((id, index) => {
    const data = survivorStatsResults[index]?.data;
    if (data) survivorStatsById.set(id, data);
  });

  const survivorLoading =
    survivorListLoading ||
    survivorPickResults.some(r => r.isLoading) ||
    survivorDetailResults.some(r => r.isLoading) ||
    survivorStatsResults.some(r => r.isLoading);

  // ── Game Grid (Bingo) ──
  // Same two-step shape as Survivor and Daily Reveal above, and for the same reason: Bingo
  // serves no "my games" endpoint, so membership is resolved contest by contest. The card
  // endpoint is both the membership test (with `committedAt` — see `buildBingoMyGames`) and the
  // data the row shows — there is no second fetch here the way Survivor needs one for its weeks.
  const { data: bingoContests = [], isLoading: bingoListLoading } = useBingoContests();
  const bingoSummaries = useMemo(() => {
    const byId = new Map(bingoContests.map(contest => [contest.id, contest]));
    // A contest entered by direct link need not be listed, and one listed for entry need not be
    // in the tenant list the app boots from — so the two sources are merged, as Survivor's are.
    for (const game of lobbyGames) {
      if (!isBingoLobbyGame(game) || byId.has(game.pickem_id)) continue;
      byId.set(game.pickem_id, {
        id: game.pickem_id,
        titleEn: game.title_en,
        sport: game.sport ?? '',
        gameType: 'bingo',
        pickemStatus: game.status,
        startUtc: game.start_utc ?? null,
      });
    }
    return [...byId.values()];
  }, [bingoContests, lobbyGames]);

  const bingoIds = useMemo(() => bingoSummaries.map(c => c.id), [bingoSummaries]);
  const bingoCardResults = useBingoCardsBatch(bingoIds);

  // Plain derivation, not `useMemo`: `useQueries` hands back a fresh results array on every
  // render, so a memo keyed on it would recompute regardless.
  const bingoCardById = new Map<string, BingoMyCard>();
  bingoIds.forEach((id, index) => {
    const data = bingoCardResults[index]?.data;
    if (data) bingoCardById.set(id, data);
  });

  const bingoLoading = bingoListLoading || bingoCardResults.some(r => r.isLoading);

  const isLoading =
    squaresLoading ||
    pickemPlayLoading ||
    pickemRecentLoading ||
    drGamesLoading ||
    survivorLoading ||
    bingoLoading;

  // ── Process Squares ──
  const activeSquaresPicks = useMemo(() => squaresPicks.filter(isSquaresActive), [squaresPicks]);
  const completedSquaresPicks = useMemo(
    () =>
      squaresPicks.filter(isSquaresCompleted).sort((a, b) => {
        const ta = a.start_time_utc ? new Date(a.start_time_utc).getTime() : 0;
        const tb = b.start_time_utc ? new Date(b.start_time_utc).getTime() : 0;
        return tb - ta;
      }),
    [squaresPicks]
  );

  // ── Process Pickem ──
  const activePickems = useMemo(
    () =>
      pickemPlay
        .filter(p => (p.has_entered || p.has_reservation) && mapPickemGameState(p) !== 'final')
        .sort((a, b) => {
          const da = a.start ? new Date(a.start as string).getTime() : 0;
          const db = b.start ? new Date(b.start as string).getTime() : 0;
          return da - db;
        }),
    [pickemPlay]
  );

  const completedPickems = useMemo(
    () =>
      pickemRecent
        .filter(p => p.has_entered || p.has_reservation)
        .sort((a, b) => {
          const da = a.start ? new Date(a.start as string).getTime() : 0;
          const db = b.start ? new Date(b.start as string).getTime() : 0;
          return db - da;
        }),
    [pickemRecent]
  );

  // ── Process Survivor ──
  // The one game type split on the ENTRY rather than the contest. Elimination is final and a
  // season runs for months, so an entrant knocked out in week 2 has nothing left to do in a
  // contest that keeps running until January — their card moves to Recent, still naming the
  // week they went out in, instead of sitting in Live implying a pick is due. Every other
  // game type resolves inside a single game and needs no such rule.
  const survivorMyGames: SurvivorMyGame[] = buildSurvivorMyGames({
    contests: survivorSummaries,
    myPicksById: survivorMyPicksById,
    detailById: survivorDetailById,
    statsById: survivorStatsById,
    lobbyGames,
  });
  const activeSurvivorGames = survivorMyGames.filter(game => !isSurvivorEntryOver(game));
  const completedSurvivorGames = survivorMyGames.filter(isSurvivorEntryOver);

  // ── Split the running contests across Upcoming / Live ──
  // Every bucket below is "not finished" by construction (the finished ones are already
  // routed to Recent), so each pair is a partition: whatever is not live is upcoming.
  // Wrapped, not point-free: `filter` passes the index as the second argument, which these
  // predicates take as `now`.
  const liveSquaresPicks = useMemo(
    () => activeSquaresPicks.filter(pick => isSquaresPickLive(pick)),
    [activeSquaresPicks]
  );
  const upcomingSquaresPicks = useMemo(
    () => activeSquaresPicks.filter(pick => !isSquaresPickLive(pick)),
    [activeSquaresPicks]
  );

  const livePickems = useMemo(() => activePickems.filter(p => isPickemLive(p)), [activePickems]);
  const upcomingPickems = useMemo(
    () => activePickems.filter(p => !isPickemLive(p)),
    [activePickems]
  );

  const liveSurvivorGames = activeSurvivorGames.filter(game => isSurvivorGameLive(game));
  const upcomingSurvivorGames = activeSurvivorGames.filter(game => !isSurvivorGameLive(game));

  // ── Process Game Grid (Bingo) ──
  // Split on the CONTEST's state, not the card's: a card that completed its line in the first
  // quarter still has a running contest to open, so it stays in Live rather than dropping into
  // Recent while the games are on. Same rule as Survivor and Squares.
  const bingoMyGames: BingoMyGame[] = buildBingoMyGames({
    contests: bingoSummaries,
    cardById: bingoCardById,
    lobbyGames,
  });
  const activeBingoGames = bingoMyGames.filter(game => !isBingoContestComplete(game));
  const completedBingoGames = bingoMyGames.filter(isBingoContestComplete);
  const liveBingoGames = activeBingoGames.filter(game => isBingoGameLive(game));
  const upcomingBingoGames = activeBingoGames.filter(game => !isBingoGameLive(game));

  /*
   * The contest's own link first; the lobby-derived one only as a fallback.
   *
   * Deriving it was the only option before the list endpoints returned `publicUrl`, and it works
   * by finding any contest of the same game type in the lobby listing and reusing its host and
   * tenant path. That makes the link conditional on the lobby listing something — so a finished
   * Game Grid contest hidden from the lobby left every player who had entered it with a card
   * that swallowed taps. Survivor has the same shape and was one hidden contest from the same
   * report; its season-long contests simply kept one listed.
   *
   * The fallback stays for responses cached before the field existed.
   */
  const bingoPlayUrl = useCallback(
    (game: BingoMyGame) =>
      buildPlayUrl(game.publicUrl ?? resolveBingoPublicUrl(game.pickemId, lobbyGames)),
    [lobbyGames]
  );

  const survivorPlayUrl = useCallback(
    (game: SurvivorMyGame) =>
      buildPlayUrl(game.publicUrl ?? resolveSurvivorPublicUrl(game.pickemId, lobbyGames)),
    [lobbyGames]
  );

  // ── Process Daily Reveal ──
  // Every selected day is classified independently off its Sportradar fixture
  // (with a reveal-lifecycle fallback until fixtures load). That keeps a still-
  // scheduled NFL kickoff in Upcoming even when another contest's day is open.
  const allSelectedDRDays = useMemo(() => collectSelectedDRDays(drGamesData), [drGamesData]);
  const selectedDRDayIds = useMemo(() => allSelectedDRDays.map(day => day.id), [allSelectedDRDays]);
  const drFixtureResults = useDailyRevealFixturesBatch(selectedDRDayIds);
  const fixturesByDayId = new Map<string, DRDayFixture[]>();
  selectedDRDayIds.forEach((id, index) => {
    const data = drFixtureResults[index]?.data;
    if (data) fixturesByDayId.set(id, data);
  });

  const upcomingDRDays = allSelectedDRDays
    .filter(day => classifyDailyRevealDayTab(day, fixturesByDayId.get(day.id)) === 'upcoming')
    .sort((a, b) => a.gameDate.localeCompare(b.gameDate));
  const liveDRDays = allSelectedDRDays
    .filter(day => classifyDailyRevealDayTab(day, fixturesByDayId.get(day.id)) === 'live')
    .sort((a, b) => a.gameDate.localeCompare(b.gameDate));
  const allDRPastDays = allSelectedDRDays
    .filter(day => classifyDailyRevealDayTab(day, fixturesByDayId.get(day.id)) === 'recent')
    .sort((a, b) => b.gameDate.localeCompare(a.gameDate));

  const resolveLeagueIpForDay = useCallback(
    (day: DRDaySummary): boolean => {
      for (const game of drGamesData) {
        if (!game) continue;
        if (game.days.some(d => d.id === day.id)) return game.game?.leagueIp ?? false;
      }
      return false;
    },
    [drGamesData]
  );

  // Recent spans every contest the user played, so the artwork folder is read from the
  // contest that owns the day rather than whichever one is currently active.
  const resolveTeamAssetFolderForDay = useCallback(
    (day: DRDaySummary): TeamAssetFolderId | null => {
      for (const game of drGamesData) {
        if (!game) continue;
        if (game.days.some(d => d.id === day.id)) {
          return readTeamAssetFolder(game.game?.styleConfig);
        }
      }
      return null;
    },
    [drGamesData]
  );

  const resolveDailyRevealDayUrl = useCallback(
    (day: DRDaySummary) => {
      const gameId = findDRGameIdForDay(day, drGamesData);
      if (!gameId) return null;
      return buildPlayUrl(buildDailyRevealContestUrl(gameId, lobbyGames));
    },
    [drGamesData, lobbyGames]
  );

  // Chronological Recent results (newest first), then optional game-type filter
  const pastResultItems = useMemo(
    () =>
      buildPastResultItems({
        dailyRevealDays: allDRPastDays,
        dailyRevealSportByDayId: drSportByDayId,
        pickems: completedPickems,
        squares: completedSquaresPicks,
        survivor: completedSurvivorGames,
        bingo: completedBingoGames,
      }),
    [
      allDRPastDays,
      drSportByDayId,
      completedPickems,
      completedSquaresPicks,
      completedSurvivorGames,
      completedBingoGames,
    ]
  );

  /**
   * Chips follow the results: Daily Reveal contributes one per sport played, so the row reads
   * "All · Gridiron Draw · Pick'em …" for an NFL-only player rather than offering three
   * Daily Reveal brands they have never entered.
   */
  const pastGameFilters = useMemo(() => buildPastGameFilters(pastResultItems), [pastResultItems]);

  // A per-sport chip can disappear when the results refresh (the last Gridiron Draw day ages
  // out). Falling back to All keeps the tab from rendering an empty list under a dead chip.
  const activePastFilter = pastGameFilters.some(filter => filter.id === pastGameFilter)
    ? pastGameFilter
    : 'all';

  const filteredPastResults = useMemo(
    () => filterPastResultItems(pastResultItems, activePastFilter),
    [pastResultItems, activePastFilter]
  );

  /**
   * Contest-week dropdown only when a Daily Reveal product chip is selected. All stays a flat
   * newest-first feed across every game type and contest week (no mid-list week control).
   */
  const recentContestWeekProduct = useMemo(() => {
    if (!activePastFilter.startsWith('daily_reveal:')) return null;
    const drItems = filteredPastResults.filter(
      (item): item is Extract<PastResultItem, { kind: 'daily_reveal' }> =>
        item.kind === 'daily_reveal'
    );
    if (drItems.length === 0) return null;
    return dailyRevealProductName(drItems[0]?.sport) || null;
  }, [activePastFilter, filteredPastResults]);

  const recentWeekOptions = useMemo(() => {
    if (!recentContestWeekProduct) return [];
    return buildDailyRevealContestWeekOptions({
      sectionItems: filteredPastResults,
      games: drGamesData,
      gameIdByDayId: drGameIdByDayId,
    });
  }, [recentContestWeekProduct, filteredPastResults, drGamesData, drGameIdByDayId]);

  const selectedRecentWeekId = resolveSelectedContestWeekId(
    recentWeekOptions,
    recentContestWeekProduct ? contestWeekByProduct[recentContestWeekProduct] : null
  );

  const displayedPastResults = useMemo(() => {
    if (!recentContestWeekProduct || !selectedRecentWeekId) return filteredPastResults;
    return filterDailyRevealItemsByContestWeek(
      filteredPastResults,
      selectedRecentWeekId,
      drGameIdByDayId
    );
  }, [filteredPastResults, recentContestWeekProduct, selectedRecentWeekId, drGameIdByDayId]);

  // Upcoming / Live share one chronological card list (no game-type headings). buildPastResultItems
  // is newest-first; Upcoming reverses to soonest-first, Live keeps newest-first.
  const upcomingTimelineItems = useMemo(() => {
    const items = buildPastResultItems({
      dailyRevealDays: upcomingDRDays,
      dailyRevealSportByDayId: drSportByDayId,
      pickems: upcomingPickems,
      squares: upcomingSquaresPicks,
      survivor: upcomingSurvivorGames,
      bingo: upcomingBingoGames,
    });
    return [...items].reverse();
  }, [
    upcomingDRDays,
    drSportByDayId,
    upcomingPickems,
    upcomingSquaresPicks,
    upcomingSurvivorGames,
    upcomingBingoGames,
  ]);

  const liveTimelineItems = useMemo(
    () =>
      buildPastResultItems({
        dailyRevealDays: liveDRDays,
        dailyRevealSportByDayId: drSportByDayId,
        pickems: livePickems,
        squares: liveSquaresPicks,
        survivor: liveSurvivorGames,
        bingo: liveBingoGames,
      }),
    [liveDRDays, drSportByDayId, livePickems, liveSquaresPicks, liveSurvivorGames, liveBingoGames]
  );

  // ── Has any data ──
  const hasLiveContent =
    liveSquaresPicks.length > 0 ||
    livePickems.length > 0 ||
    liveSurvivorGames.length > 0 ||
    liveBingoGames.length > 0 ||
    liveDRDays.length > 0;

  const hasUpcomingContent =
    upcomingSquaresPicks.length > 0 ||
    upcomingPickems.length > 0 ||
    upcomingSurvivorGames.length > 0 ||
    upcomingBingoGames.length > 0 ||
    upcomingDRDays.length > 0;

  const hasPastContent = pastResultItems.length > 0;

  // ─────────────────────────────────────────────────────────────────────────────

  /** One finished contest's card on Recent (graded Daily Reveal face). */
  const renderPastResultCard = (item: PastResultItem) => {
    if (item.kind === 'daily_reveal') {
      return (
        <DRPastDayCard
          key={pastItemKey(item)}
          day={item.day}
          sport={item.sport}
          leagueIpEnabled={resolveLeagueIpForDay(item.day)}
          teamAssetFolder={resolveTeamAssetFolderForDay(item.day)}
          targetUrl={resolveDailyRevealDayUrl(item.day)}
        />
      );
    }

    if (item.kind === 'survivor') {
      return (
        <LobbySurvivorCard
          key={pastItemKey(item)}
          game={item.game}
          publicUrl={survivorPlayUrl(item.game)}
        />
      );
    }

    if (item.kind === 'bingo') {
      return (
        <LobbyBingoCard
          key={pastItemKey(item)}
          game={item.game}
          publicUrl={bingoPlayUrl(item.game)}
        />
      );
    }

    if (item.kind === 'pickem') {
      const pickemId = item.pickem.pickem_id as string;
      const rawUrl = publicUrlMap.get(pickemId) ?? resolvePickemPublicUrl(pickemId, lobbyGames);
      return (
        <LobbyPickemCard
          key={pastItemKey(item)}
          pickem={item.pickem}
          publicUrl={buildPlayUrl(rawUrl)}
        />
      );
    }

    const baseUrl = buildSquaresPickPlayUrl(item.pick, lobbyGames);
    const resultsUrl = baseUrl ? `${baseUrl}&mode=results` : null;
    const scoreId = squaresContestScoreId(item.pick);
    return (
      <LobbySquaresCard
        key={pastItemKey(item)}
        pick={item.pick}
        publicUrl={resultsUrl}
        scores={scoreId ? squaresScoresByContestId.get(scoreId) : null}
      />
    );
  };

  /** Upcoming / Live cards — Daily Reveal uses the live/"today" face. */
  const renderRunningResultCard = (item: PastResultItem) => {
    if (item.kind === 'daily_reveal') {
      return (
        <DRTodaysPickSection
          key={pastItemKey(item)}
          currentDay={item.day}
          sport={item.sport}
          leagueIpEnabled={resolveLeagueIpForDay(item.day)}
          teamAssetFolder={resolveTeamAssetFolderForDay(item.day)}
          targetUrl={resolveDailyRevealDayUrl(item.day)}
        />
      );
    }
    return renderPastResultCard(item);
  };

  /**
   * One tab's chronological card list. Upcoming and Live no longer group by game type —
   * product identity lives on each card (contest title / shell).
   */
  const renderContestTimeline = (bucket: 'upcoming' | 'live') => {
    const items = bucket === 'live' ? liveTimelineItems : upcomingTimelineItems;
    return <div className="space-y-3">{items.map(renderRunningResultCard)}</div>;
  };

  return (
    <>
      {/* Sub-tabs — segmented control */}
      <div
        role="tablist"
        aria-label="My Games"
        className="mb-6 grid grid-cols-3 gap-1 rounded-full border border-white/10 bg-black/40 p-1"
      >
        {MY_GAMES_TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={[
              'rounded-full px-3 py-2 text-sm font-semibold transition',
              activeTab === tab.id
                ? 'bg-white/15 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]'
                : 'text-gray-400 hover:text-white',
            ].join(' ')}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-[#53d337] mb-3" />
          <p className="text-zinc-400 text-sm">Loading your games…</p>
        </div>
      )}

      {/* Upcoming Tab */}
      {!isLoading &&
        activeTab === 'upcoming' &&
        (!hasUpcomingContent ? (
          <EmptyState
            icon={<CalendarClock className="w-8 h-8 text-zinc-600" />}
            title="No Upcoming Games"
            message="Games you've entered will display here before they begin."
            actionLabel="Browse Lobby"
            onAction={onBrowseLobby}
          />
        ) : (
          renderContestTimeline('upcoming')
        ))}

      {/* Live Tab */}
      {!isLoading &&
        activeTab === 'live' &&
        (!hasLiveContent ? (
          <EmptyState
            icon={<Grid3X3 className="w-8 h-8 text-zinc-600" />}
            title="No Live Games"
            message="You're not playing any contests right now. Browse the lobby to join one!"
            actionLabel="Browse Lobby"
            onAction={onBrowseLobby}
          />
        ) : (
          renderContestTimeline('live')
        ))}

      {/* Recent Tab */}
      {!isLoading && activeTab === 'recent' && (
        <>
          {!hasPastContent ? (
            <EmptyState
              icon={<Trophy className="w-8 h-8 text-zinc-600" />}
              title="No Recent Results"
              message="Results from completed games will appear here once you've participated."
              actionLabel="Browse Lobby"
              onAction={onBrowseLobby}
            />
          ) : (
            <div>
              {/* Game type filter — All shows every section, a chip narrows to one */}
              <div
                className="mb-5 flex items-center gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                role="tablist"
                aria-label="Filter recent results by game type"
              >
                {pastGameFilters.map(filter => (
                  <button
                    key={filter.id}
                    type="button"
                    role="tab"
                    aria-selected={activePastFilter === filter.id}
                    onClick={() => setPastGameFilter(filter.id)}
                    className={[
                      'shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition',
                      activePastFilter === filter.id
                        ? 'border-white bg-white/10 text-white'
                        : 'border-white/20 bg-black/25 text-gray-300 hover:border-white/45 hover:text-white',
                    ].join(' ')}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {filteredPastResults.length === 0 ? (
                <EmptyState
                  icon={<Trophy className="w-8 h-8 text-zinc-600" />}
                  title="No Matching Results"
                  message="No recent results for this game type. Try another filter."
                />
              ) : (
                <div className="space-y-3">
                  {recentContestWeekProduct && selectedRecentWeekId && (
                    <DailyRevealContestWeekSelect
                      items={recentWeekOptions}
                      selectedId={selectedRecentWeekId}
                      onSelect={id =>
                        setContestWeekByProduct(prev => ({
                          ...prev,
                          [recentContestWeekProduct]: id,
                        }))
                      }
                      ariaLabel={`${recentContestWeekProduct} contest week`}
                    />
                  )}
                  {displayedPastResults.map(renderPastResultCard)}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}

// Make sure React is imported for JSX (needed for older TS configs)
import React from 'react';
